import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { PrivacyPolicyDialog } from "@/components/auth/PrivacyPolicyDialog";
import { OtpCodeField } from "@/components/auth/OtpCodeField";
import { TermsAndConditionsDialog } from "@/components/auth/TermsAndConditionsDialog";
import { AuthPageBrand } from "@/components/shared/AuthPageBrand";
import { PhoneCountryField } from "@/components/auth/PhoneCountryField";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, ArrowLeft } from "lucide-react";
import { sanitizeEmailInput, sanitizeTextInput } from "@/lib/security";
import { TERMS_VERSION } from "@/lib/termsAndConditions";
import { AUTH_LOGO_INLINE } from "@/lib/authLogoInline";
import { getPasswordPolicyErrorMessage, getPasswordRuleItems, passwordMeetsAllRules } from "@/lib/passwordPolicy";
import { PHONE_COUNTRIES, getPhoneCountryByIso2 } from "@/lib/phoneCountries";

const SignupPage = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneCountryIso2, setPhoneCountryIso2] = useState("CO");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mfaToken, setMfaToken] = useState("");
  const [mfaPin, setMfaPin] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
  const [submitAfterTerms, setSubmitAfterTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const mfaPinRef = useRef("");
  const verifyMfaInFlightRef = useRef(false);
  const { register, verifyMfa, resendMfa } = useAuth();
  const { language, t, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const passwordRuleItems = getPasswordRuleItems(language, password);
  const passwordRulesSatisfied = passwordMeetsAllRules(password);
  const confirmPasswordStarted = confirmPassword.length > 0;
  const passwordsMatch = password === confirmPassword;
  const selectedPhoneCountry = getPhoneCountryByIso2(phoneCountryIso2);
  const sanitizedFirstName = sanitizeTextInput(firstName, 80);
  const sanitizedLastName = sanitizeTextInput(lastName, 80);
  const normalizedPhoneNumber = phoneNumber.replace(/\D/g, "").slice(0, 15);
  const firstNameReady = sanitizedFirstName.trim().length >= 2;
  const lastNameReady = sanitizedLastName.trim().length >= 2;
  const emailReady = email.trim().length > 0;
  const phoneReady = normalizedPhoneNumber.length >= 7;
  const canSubmitSignup = !loading && firstNameReady && lastNameReady && emailReady && phoneReady && passwordRulesSatisfied && confirmPasswordStarted && passwordsMatch && termsAccepted;

  const getSignupErrorMessage = (error) => {
    const detail = error?.response?.data?.detail ?? error?.response?.data ?? null;

    if (typeof detail === "string" && detail.trim()) {
      return detail;
    }

    if (Array.isArray(detail)) {
      const messages = detail
        .map((item) => {
          if (typeof item === "string") return item;
          if (item && typeof item === "object") return item.msg || item.message || null;
          return null;
        })
        .filter(Boolean);

      if (messages.length) return messages.join(" · ");
    }

    if (detail && typeof detail === "object") {
      return detail.msg || detail.message || (language === "es" ? "No se pudo crear la cuenta." : "Could not create account.");
    }

    if (error?.code === "ERR_NETWORK") {
      return language === "es"
        ? "No se pudo conectar con el servidor. Intenta de nuevo."
        : "Could not reach the server. Please try again.";
    }

    return language === "es" ? "No se pudo crear la cuenta." : "Could not create account.";
  };

  const validateSignupForm = () => {
    if (!firstNameReady || !lastNameReady) {
      toast.error(language === "es" ? "Ingresa nombre y apellido válidos" : "Enter a valid first and last name");
      return false;
    }

    if (!phoneReady) {
      toast.error(language === "es" ? "Ingresa un número de teléfono válido" : "Enter a valid phone number");
      return false;
    }

    if (password !== confirmPassword) {
      toast.error(language === "es" ? "Las contraseñas no coinciden" : "Passwords do not match");
      return false;
    }

    if (!passwordRulesSatisfied) {
      toast.error(getPasswordPolicyErrorMessage(language));
      return false;
    }

    return true;
  };

  const submitSignup = async () => {
    setLoading(true);

    try {
      const result = await register(email, password, {
        firstName: sanitizedFirstName,
        lastName: sanitizedLastName,
        phoneCountryIso2: selectedPhoneCountry.iso2,
        phoneCountryCode: selectedPhoneCountry.dialCode,
        phoneNumber: normalizedPhoneNumber,
        termsAccepted: true,
        termsVersion: TERMS_VERSION,
      });

      if (result.mfaRequired) {
        setMfaToken(result.mfaToken);
        setPendingEmail(result.email || email);
        setMfaPin("");
        toast.success(result.message || (language === "es" ? "Te enviamos un PIN para validar tu correo." : "We sent you a PIN to verify your email."));
        return;
      }

      toast.success("Account created successfully!");
      navigate("/dashboard");
    } catch (error) {
      console.error("Signup error:", error);
      toast.error(String(getSignupErrorMessage(error)));
    } finally {
      setLoading(false);
      setSubmitAfterTerms(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateSignupForm()) {
      return;
    }

    if (!termsAccepted) {
      setSubmitAfterTerms(true);
      setTermsDialogOpen(true);
      toast.error(language === "es" ? "Debes aceptar los Términos y Condiciones para crear tu cuenta" : "You must accept the Terms and Conditions to create your account");
      return;
    }

    await submitSignup();
  };

  const handleAcceptTerms = async () => {
    setTermsAccepted(true);
    setTermsDialogOpen(false);
    toast.success(language === "es" ? "Términos y Condiciones aceptados" : "Terms and Conditions accepted");

    if (submitAfterTerms && validateSignupForm()) {
      await submitSignup();
    }
  };

  const handleMfaPinChange = (nextPin) => {
    mfaPinRef.current = nextPin;
    setMfaPin(nextPin);
  };

  const handleVerifyMfa = async (e) => {
    e.preventDefault();
    if (loading || verifyMfaInFlightRef.current) {
      return;
    }

    const stablePin = String(mfaPinRef.current || mfaPin || "").replace(/\D/g, "").slice(0, 6);
    if (stablePin.length !== 6) {
      toast.error(language === "es" ? "Ingresa el PIN de 6 dígitos" : "Enter the 6-digit PIN");
      return;
    }

    verifyMfaInFlightRef.current = true;
    setLoading(true);
    try {
      await verifyMfa(mfaToken, stablePin);
      toast.success(language === "es" ? "Correo validado correctamente" : "Email verified successfully");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error?.response?.data?.detail || (language === "es" ? "No se pudo validar el PIN" : "Could not verify the PIN"));
    } finally {
      verifyMfaInFlightRef.current = false;
      setLoading(false);
    }
  };

  const handleResendMfa = async () => {
    setLoading(true);
    try {
      await resendMfa(mfaToken);
      mfaPinRef.current = "";
      setMfaPin("");
      toast.success(language === "es" ? "Te enviamos un nuevo PIN" : "We sent you a new PIN");
    } catch (error) {
      toast.error(error?.response?.data?.detail || (language === "es" ? "No se pudo reenviar el PIN" : "Could not resend the PIN"));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setMfaToken("");
    setMfaPin("");
    mfaPinRef.current = "";
    setPendingEmail("");
  };

  return (
    <div
      className="min-h-screen overflow-y-auto bg-background flex items-start md:items-center justify-center px-4 sm:px-6 py-8 md:py-10 hero-gradient"
      data-testid="signup-page"
      lang={language}
      translate="no"
    >
      <TermsAndConditionsDialog
        open={termsDialogOpen}
        onOpenChange={setTermsDialogOpen}
        onAccept={handleAcceptTerms}
        language={language}
      />
      <PrivacyPolicyDialog
        open={privacyDialogOpen}
        onOpenChange={setPrivacyDialogOpen}
        language={language}
      />

      <AuthPageBrand testIdPrefix="signup-page-brand" />

      {/* Language Toggle */}
      <div className="fixed top-4 right-4 flex items-center" data-testid="language-toggle">
        <button
          onClick={() => toggleLanguage("es")}
          className={`px-3 py-1.5 text-sm font-bold transition-all ${
            language === "es"
              ? "bg-primary text-primary-foreground"
              : "bg-muted/50 text-muted-foreground hover:text-foreground"
          }`}
          data-testid="signup-lang-es-btn"
        >
          ES
        </button>
        <button
          onClick={() => toggleLanguage("en")}
          className={`px-3 py-1.5 text-sm font-bold transition-all ${
            language === "en"
              ? "bg-primary text-primary-foreground"
              : "bg-muted/50 text-muted-foreground hover:text-foreground"
          }`}
          data-testid="signup-lang-en-btn"
        >
          EN
        </button>
      </div>

      <Card className="notranslate w-full max-w-lg bg-card border-border p-6 md:p-7" translate="no">
        <div className="text-center mb-6 md:mb-7">
          <Link to="/" className="inline-flex flex-col items-center gap-3 mb-6" data-testid="signup-card-logo-link">
            <img
              src={AUTH_LOGO_INLINE}
              alt="Voxara"
              className="h-28 sm:h-32 md:h-36 w-auto max-w-full object-contain"
              width="960"
              height="640"
              loading="eager"
              fetchPriority="high"
              decoding="sync"
              data-testid="signup-card-logo-image"
            />
          </Link>
          <h1 className="text-2xl font-bold">
            {mfaToken ? (language === "es" ? "Valida tu correo" : "Verify your email") : t("createAccount")}
          </h1>
          <p className="text-muted-foreground mt-2">
            {mfaToken
              ? (language === "es"
                  ? `Ingresa el PIN enviado a ${pendingEmail}`
                  : `Enter the PIN sent to ${pendingEmail}`)
              : t("startTranscribing")}
          </p>
        </div>

        {!mfaToken ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="firstName">{language === "es" ? "Nombre" : "First name"}</Label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder={language === "es" ? "Tu nombre" : "Your first name"}
                  value={firstName}
                  onChange={(e) => setFirstName(sanitizeTextInput(e.target.value, 80))}
                  required
                  maxLength={80}
                  autoComplete="given-name"
                  className="bg-background border-border focus:border-primary"
                  data-testid="signup-first-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">{language === "es" ? "Apellido" : "Last name"}</Label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder={language === "es" ? "Tu apellido" : "Your last name"}
                  value={lastName}
                  onChange={(e) => setLastName(sanitizeTextInput(e.target.value, 80))}
                  required
                  maxLength={80}
                  autoComplete="family-name"
                  className="bg-background border-border focus:border-primary"
                  data-testid="signup-last-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t("email")}</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(sanitizeEmailInput(e.target.value))}
                required
                maxLength={254}
                autoComplete="email"
                className="bg-background border-border focus:border-primary"
                data-testid="signup-email-input"
              />
            </div>

            <PhoneCountryField
              language={language}
              countries={PHONE_COUNTRIES}
              selectedCountry={selectedPhoneCountry}
              phoneNumber={phoneNumber}
              onCountryChange={setPhoneCountryIso2}
              onPhoneChange={(value) => setPhoneNumber(value.replace(/\D/g, "").slice(0, 15))}
            />

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="password">{t("password")}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(sanitizeTextInput(e.target.value, 128))}
                    required
                    minLength={8}
                    maxLength={128}
                    autoComplete="new-password"
                    className="bg-background border-border focus:border-primary pr-10"
                    data-testid="signup-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    data-testid="signup-password-visibility-button"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="space-y-1" data-testid="signup-password-rule-status">
                  <div className="space-y-1" data-testid="signup-password-rule-list">
                    {passwordRuleItems.map((rule) => (
                      <p
                        key={rule.key}
                        className={`text-xs font-medium transition-colors ${rule.satisfied ? "text-emerald-600" : "text-amber-600"}`}
                        data-testid={`signup-password-${rule.key}-message`}
                      >
                        {rule.satisfied ? "✓ " : "• "}
                        {rule.label}
                      </p>
                    ))}
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted" data-testid="signup-password-strength-bar-track">
                    <div
                      className={`h-full rounded-full transition-all ${passwordRulesSatisfied ? "bg-emerald-500" : "bg-amber-500"}`}
                      style={{ width: `${(passwordRuleItems.filter((rule) => rule.satisfied).length / passwordRuleItems.length) * 100}%` }}
                      data-testid="signup-password-strength-bar-fill"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t("confirmPassword")}</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(sanitizeTextInput(e.target.value, 128))}
                  required
                  maxLength={128}
                  autoComplete="new-password"
                  className="bg-background border-border focus:border-primary"
                  data-testid="signup-confirm-password-input"
                />
                <p
                  className={`text-xs font-medium transition-colors ${!confirmPasswordStarted ? "text-muted-foreground" : passwordsMatch ? "text-emerald-600" : "text-destructive"}`}
                  data-testid="signup-confirm-password-message"
                >
                  {!confirmPasswordStarted
                    ? (language === "es" ? "Confirma tu contraseña para habilitar el registro" : "Confirm your password to enable signup")
                    : passwordsMatch
                      ? (language === "es" ? "✓ Las contraseñas coinciden" : "✓ Passwords match")
                      : (language === "es" ? "Las contraseñas no coinciden" : "Passwords do not match")}
                </p>
              </div>
            </div>

            <div className="space-y-3 rounded-2xl border border-border bg-muted/30 p-4" data-testid="signup-terms-summary-card">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">
                    {language === "es" ? "Términos y Condiciones" : "Terms and Conditions"}
                  </p>
                  <p className="text-xs text-muted-foreground" data-testid="signup-terms-status-text">
                    {termsAccepted
                      ? (language === "es" ? "Ya aceptaste los términos de Voxara para esta cuenta." : "You already accepted Voxara’s terms for this account.")
                      : (language === "es" ? "Debes leer y aceptar los términos antes de crear tu cuenta." : "You must read and accept the terms before creating your account.")}
                  </p>
                </div>
                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${termsAccepted ? "bg-emerald-500/15 text-emerald-600" : "bg-amber-500/15 text-amber-600"}`}
                  data-testid="signup-terms-status-badge"
                >
                  {termsAccepted ? (language === "es" ? "Aceptados" : "Accepted") : (language === "es" ? "Pendiente" : "Pending")}
                </span>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => setTermsDialogOpen(true)}
                data-testid="signup-open-terms-button"
                  translate="no"
              >
                {termsAccepted
                  ? (language === "es" ? "Ver términos aceptados" : "View accepted terms")
                  : (language === "es" ? "Leer y aceptar términos" : "Read and accept terms")}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => setPrivacyDialogOpen(true)}
                data-testid="signup-open-privacy-button"
                  translate="no"
              >
                {language === "es" ? "Leer política de privacidad" : "Read privacy policy"}
              </Button>
            </div>

            <Button
              type="submit"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 btn-glow"
              disabled={!canSubmitSignup}
              data-testid="signup-submit-btn"
              translate="no"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("creatingAccount")}
                </>
              ) : (
                t("createAccountBtn")
              )}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleVerifyMfa} className="space-y-6">
            <div className="space-y-2">
              <Label>{language === "es" ? "PIN de validación" : "Verification PIN"}</Label>
              <div className="flex justify-center">
                <OtpCodeField
                  value={mfaPin}
                  onChange={handleMfaPinChange}
                  testId="signup-mfa-pin-input"
                  fallbackTestId="signup-mfa-pin-input-fallback"
                  ariaLabel="Signup verification PIN"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 btn-glow"
              disabled={loading}
              data-testid="signup-mfa-verify-button"
              translate="no"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {language === "es" ? "Validando..." : "Verifying..."}
                </>
              ) : (
                language === "es" ? "Validar PIN" : "Verify PIN"
              )}
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleResendMfa}
              disabled={loading}
              data-testid="signup-mfa-resend-button"
              translate="no"
            >
              {language === "es" ? "Reenviar PIN" : "Resend PIN"}
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={handleBack}
              disabled={loading}
              data-testid="signup-mfa-back-button"
              translate="no"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === "es" ? "Volver" : "Back"}
            </Button>
          </form>
        )}

        <div className="mt-6 text-center text-sm text-muted-foreground">
          {t("haveAccount")}{" "}
          <Link
            to="/login"
            className="text-primary hover:underline font-medium"
          >
            {t("signIn")}
          </Link>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground" data-testid="signup-legal-links-row">
          <Link to="/terms" className="hover:text-foreground" data-testid="signup-legal-terms-link">
            {language === "es" ? "Términos y Condiciones" : "Terms and Conditions"}
          </Link>
          <Link to="/privacy" className="hover:text-foreground" data-testid="signup-legal-privacy-link">
            {language === "es" ? "Política de Privacidad" : "Privacy Policy"}
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default SignupPage;
